import React from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import TeamLogo from "../../_partials/TeamLogo";
import getContentById from "../../../../shared/utils/getContentById";
import { PresetColors } from "../../../../shared/components/MediaLibrary/EditModal/LeftSidebar/EditTab";
import { fcbId } from "../ImageTable/Index";
// Shared by all the matchday sliders -- its own home, per the note on it.
// The `t`-taking variant, because the circles below are mapped over feed data
// whose length changes between renders; see the note on it in helpers.js.
import { oneLetterResult } from "../MatchdaySlider/helpers";

// Everything this template paints on top of its background layers.
//
// The editor canvas draws those layers itself as draggable boxes, so it renders
// this overlay alone (see EditModal/slider2Content.js) while the template's
// Index.jsx renders it over its own static copy of them. Both get the same
// markup and the same bar arithmetic from here, which is what stops the preview
// and the exported image drifting apart.
//
// `manualEntries` is a prop rather than something read off the template: in the
// editor it has to follow what the person is typing right now, while the view
// takes the copy saved on the template.
const SliderOverlay = ({ data, jersey, hasCLStars, isDfbPokal, manualEntries }) => {
  const { t } = useTranslation();

  const { fields } = data.data || {};
  const { totalScores, homeTeamScorers, awayTeamScorers, homeLastGames, awayLastGames } =
    data?.data?.dynamic || {};
  const selectedLang = data?.lang;
  const competitionId = data?.event?.competitionId;

  const homeTeam = data?.event?.home;
  const awayTeam = data?.event?.away;

  let fcbTeam = homeTeam;
  let opponentTeam = awayTeam;
  if (awayTeam.id === fcbId) {
    fcbTeam = awayTeam;
    opponentTeam = homeTeam;
  }

  let quoteText = getContentById("quote", fields);
  let authorText = getContentById("author", fields);

  let numberOfWins = totalScores?.homeWins || 0;
  let numberOfDraws = totalScores?.homeDraws || 0;
  let numberOfLosses = totalScores?.homeLoss || 0;
  if (isNaN(numberOfWins)) {
    numberOfWins = 0;
  }
  if (isNaN(numberOfDraws)) {
    numberOfDraws = 0;
  }
  if (isNaN(numberOfLosses)) {
    numberOfLosses = 0;
  }

  if (!!manualEntries) {
    const { win, draw, loss, quote, author } = manualEntries;

    const valid =
      [win, draw, loss].some(v => v !== 0) &&    // at least one is not zero
      [win, draw, loss].every(v => !isNaN(v));   // none are NaN

    if (valid) {
      // safe to use manualEntries
      numberOfWins = manualEntries.win;
      numberOfDraws = manualEntries.draw;
      numberOfLosses = manualEntries.loss;
    }
    if (quote && quote !== "") {
      quoteText = quote;
    }
    if (author && author !== "") {
      authorText = author;
    }
  }

  const numberOfMatches =
    Math.max(numberOfWins, 0.5) + Math.max(numberOfDraws, 0.5) + Math.max(numberOfLosses, 0.5);

  // The variant every coloured thing on this overlay keys off -- text, strokes,
  // rules, and the scorer tables' rauten bands.
  //
  // It is NOT the kit alone. A cup or European tie replaces the canvas artwork
  // outright (background.js), and the type has to follow it there: leaving a
  // white-kit fixture on `white` would paint #de0034 text over the UCL and DFB
  // artwork, which is neither legible nor the design. So the precedence here is
  // the same chain the background uses -- Pokal, then CL, then the kit -- and
  // style.scss carries a treatment for each of the five.
  const variantClass = isDfbPokal
    ? "dfb"
    : hasCLStars
      ? "ucl"
      : jersey === PresetColors.white
        ? "white"
        : jersey === PresetColors.red
          ? "red"
          : "black";

  const mapValue = (val, inMin = 8, inMax = 76, outMin = 52, outMax = 92) => {
    const mapped =
      ((val - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;

    // clamp between outMin and outMax
    return Math.min(Math.max(mapped, outMin), outMax);
  };

  const totalWidthInPixel = 1100;

  // No segment may be narrower than the crest that sits on it. The crest is
  // 70px wide but offset (`left: calc(50% - 40px)`), so it spans
  // [W/2 - 40, W/2 + 30] and needs 80px of segment to sit inside rather than
  // overhang its own bar.
  const minWidthInPixel = 80;

  // Clamping alone will not hold: the segments are flex items with the default
  // flex-shrink, so widths summing past totalWidthInPixel get shrunk straight
  // back under the floor. So lift whoever is short up to it and take those
  // pixels out of the segments that still have room, in proportion. Flooring
  // one segment can push the next under, hence the pass per segment.
  const shareWidths = (counts, total, minimum) => {
    let atFloor = counts.map(() => false);
    let widths = counts.map(() => minimum);
    for (let pass = 0; pass < counts.length; pass += 1) {
      const budget = total - atFloor.filter(Boolean).length * minimum;
      const pool = counts.reduce((sum, c, i) => (atFloor[i] ? sum : sum + c), 0);
      widths = counts.map((c, i) => {
        if (atFloor[i]) return minimum;
        return pool > 0 ? (c / pool) * budget : budget / counts.length;
      });
      const next = widths.map((w, i) => atFloor[i] || w < minimum);
      if (next.every((v, i) => v === atFloor[i])) break;
      atFloor = next;
    }
    return widths;
  };

  const [winWidth, drawWidth, lossWidth] = shareWidths(
    [
      Math.max(numberOfWins, 0.5),
      Math.max(numberOfDraws, 0.5),
      Math.max(numberOfLosses, 0.5),
    ],
    totalWidthInPixel,
    minWidthInPixel,
  );
  const drawFakePolygon = (100 - mapValue((Math.max(numberOfWins, 0.5) / numberOfMatches) * 100)) / 100;
  const drawFakeBarWidth = winWidth * drawFakePolygon;
  const cutWidthPixel = 40;

  const mapValuePixel = (_width) => {
    return _width - cutWidthPixel;
  }

  // How far a segment's letter slides ALONG its own segment, in screen px
  // (+ is down the column).
  //
  // Given .label's fixed square box, a letter lands baseline-first 72px up
  // from its segment's bottom end and stands 210px tall. A segment shorter
  // than that just cuts the letter's top off -- which still leaves the L its
  // foot and the D its bowl, enough to read it by. Once a segment is long
  // enough to hold the whole glyph, lift it to sit centred instead. Clamped at
  // 0 so a short segment never slides the letter's bottom off its own bar.
  //
  // The two constants are measured from the 320px type; they track .label's
  // font-size in style.scss, so if that changes, re-measure.
  const labelBaselineLift = 72;
  const labelCapHeight = 210;
  const labelTransform = (segmentWidth) => ({
    transform: `rotate(90deg) translate(0, ${Math.min(
      0,
      labelBaselineLift + labelCapHeight / 2 - segmentWidth / 2,
    )}px)`,
  });

  // The bevels below are cut along the bar's BOTTOM edge (100%), not the top.
  // style.scss turns the whole bar -90deg, which maps the bar's local y-axis
  // onto the column's screen x-axis -- so the bottom edge is what reads as the
  // column's right-hand side. Mirror every y in these polygons (0 <-> 100%,
  // 30% <-> 70%) to put the bevels back on the left.

  return (
    <>
      <div className={`${variantClass}`}></div>
      <div class="match-stats-container-wrap">
        <div class="match-stats-container">
          <div
            class="progress-segment wins"
            style={{
              width: `${winWidth}px`,
            }}
          >
            <div className={`logo`}>
              <TeamLogo team={fcbTeam} competitionId={competitionId} />
            </div>
            <div className="bar"
              style={{
                clipPath: `polygon(0 0, 0 30%, 0 100%, ${mapValuePixel(winWidth)}px 100%, 100% 70%, 100% 0)`
              }}>
              {/* The letter lives INSIDE .bar so the bar's clip-path trims it:
                clip-path clips an element's whole subtree, so the 320px glyph
                is cut to the bar's real shape, bevel included, instead of
                spilling over the neighbouring segments. It is positioned
                absolutely, so .value's offset counts from .bar, not from it. */}
              <div class={`label ${variantClass}`} style={labelTransform(winWidth)}>
                {t("W", { keyPrefix: "table", lng: selectedLang })}
              </div>
            </div>
            <div class={`value ${variantClass}`}>{numberOfWins}</div>
            <div class={`label-full ${variantClass}`}>{t("Wins", { keyPrefix: "table", lng: selectedLang })}</div>
          </div>
          <div
            class="progress-segment draws"
            style={{
              width: `${drawWidth}px`
            }}
          >
            <div
              className="bar"
              style={{
                clipPath: `polygon(0 0, 100% 0, 100% 70%, ${mapValuePixel(drawWidth)}px 100%, 0 100%)`
              }}
            >
              {/* The letter lives INSIDE .bar so the bar's clip-path trims it:
                clip-path clips an element's whole subtree, so the 320px glyph
                is cut to the bar's real shape, bevel included, instead of
                spilling over the neighbouring segments. It is positioned
                absolutely, so .value's offset counts from .bar, not from it. */}
              <div class={`label ${variantClass}`} style={labelTransform(drawWidth)}>{t("D", { keyPrefix: "table", lng: selectedLang })}</div>
            </div>
            <div className="bar-fake"
              style={{
                width: `${drawFakeBarWidth}px`,
                left: `calc(${-drawFakeBarWidth + 2}px)`
              }}>
            </div>
            <div class={`value ${variantClass}`}>{numberOfDraws}</div>
            <div class={`label-full ${variantClass}`}>{t("Draws", { keyPrefix: "table", lng: selectedLang })}</div>
          </div>
          <div
            class="progress-segment losses"
            style={{
              width: `${lossWidth}px`
            }}
          >
            <div className={`logo`}>
              <TeamLogo team={opponentTeam} competitionId={competitionId} />
            </div>
            <div
              className="bar"
              style={{
                clipPath: `polygon(0 0, 100% 0, 100% 70%, ${mapValuePixel(lossWidth)}px 100%, 0 100%)`
              }}>
              {/* The letter lives INSIDE .bar so the bar's clip-path trims it:
                clip-path clips an element's whole subtree, so the 320px glyph
                is cut to the bar's real shape, bevel included, instead of
                spilling over the neighbouring segments. It is positioned
                absolutely, so .value's offset counts from .bar, not from it. */}
              <div class={`label ${variantClass}`} style={labelTransform(lossWidth)}>{t("L", { keyPrefix: "table", lng: selectedLang })}</div>
            </div>
            <div
              className="bar-fake"
              style={{
                width: `calc(${drawWidth * mapValue((Math.max(numberOfDraws, 0.5) / numberOfMatches)) / 100 + 2}px)`,
                left: `calc(${- drawWidth * mapValue((Math.max(numberOfDraws, 0.5) / numberOfMatches)) / 100}px)`
              }}>
            </div>
            <div class={`value ${variantClass}`}>{numberOfLosses}</div>
            <div class={`label-full ${variantClass}`}>{t("Losses", { keyPrefix: "table", lng: selectedLang })}</div>
          </div>
        </div>
      </div>
      <div className="goal-player-container">
        <div className="home-team-table-head">
          <div className={`goals text-label ${variantClass}`}>
            {t("Goals", { keyPrefix: "table", lng: selectedLang })}
          </div>
        </div>
        <div className="goal-player-home">
          {/* home team logo */}
          <div className="home-team-logo-container">
            <TeamLogo team={homeTeam} competitionId={competitionId} />
          </div>

          {/* home team top scorers */}
          <div className={`home-team-table ${variantClass}`}>
            {
              homeTeamScorers?.map((entry, index) => {
                return (
                  <div className="home-team-table-row" key={index}>
                    <div className={`player-name ${variantClass}`}>
                      {entry.player.name}
                    </div>
                    <div className={`goals text-value ${variantClass}`}>
                      {entry.goals}
                    </div>
                  </div>
                )
              })
            }

          </div>
        </div>

        <div className="goal-player-away">
          {/* home team logo */}
          <div className="home-team-logo-container">
            <TeamLogo team={awayTeam} competitionId={competitionId} />
          </div>
          {/* home team top scorers */}
          <div className={`home-team-table ${variantClass}`}>
            {
              awayTeamScorers?.map((entry, index) => {
                return (
                  <div className="home-team-table-row" key={index}>
                    <div className={`player-name ${variantClass}`}>
                      {entry.player.name}
                    </div>
                    <div className={`goals text-value ${variantClass}`}>
                      {entry.goals}
                    </div>
                  </div>
                )
              })
            }

          </div>
        </div>
      </div>

      {/* Match-up lockup: the two club names stacked, with an outlined VS
          sitting behind them.
          
          The home slot is deliberately the static club name rather than the
          fixture's home side: Bayern is to read first whether the fixture has
          them at home or away. So the away slot takes `opponentTeam`, which is
          resolved above by looking for Bayern on either side -- not
          `awayTeam`, which would print Bayern's own name back at them for an
          away fixture. */}
      <div className="matchup-container">
        <div className={`matchup-vs ${variantClass}`}>VS</div>
        <div className="matchup-names">
          <div className={`matchup-home ${variantClass}`}>FC Bayern</div>
          <div className={`matchup-away ${variantClass}`}>{opponentTeam?.name}</div>
        </div>
      </div>

      {/* The last-games row, copied from fcb_matchday_slider_1. Its data is the
          same pair that template fetches -- homeLastGames / awayLastGames off
          the dynamic block, which fcb_matchday_slider_2's getDynamicData now
          returns too.

          Guarded on the arrays: the reverse() below mutates, so it needs a copy,
          and a feed failure leaves these undefined rather than empty. */}
      <div className="first-bottom-container">
        <div className={`last-games ${variantClass}`}>
          <div className="left-circles">
            {[...(homeLastGames || [])].reverse().map((result, index) => {
              const out = oneLetterResult(t, result, homeTeam?.id, selectedLang);
              return <div className={`circle ${variantClass}`} key={index}>{out}</div>;
            })}
          </div>

          <div className="home-away-container">
            {/* Sits behind the two crests, centred across both. Same outlined
                treatment as the .matchup-vs lockup at the foot of the canvas. */}
            <div className={`vs ${variantClass}`}>VS</div>

            <div className="home">
              <TeamLogo team={homeTeam} competitionId={competitionId} />
            </div>

            <div className="away">
              <TeamLogo team={awayTeam} competitionId={competitionId} />
            </div>
          </div>

          <div className="right-circles">
            {(awayLastGames || []).map((result, index) => {
              const out = oneLetterResult(t, result, awayTeam?.id, selectedLang);
              return <div className={`circle ${variantClass}`} key={index}>{out}</div>;
            })}
          </div>
        </div>
      </div>

      {/* <div className="custom-quote-container">
        <div className="quote-shape-wrapper">
          <div className={`quote-shape-box ${variantClass}`}></div>
          <div className={`quote-shape-box ${variantClass}`}></div>
        </div>
        <div className={`quote-text ${variantClass}`}>
          {quoteText}
        </div>
        <div className="quote-bottom">
          <div className={`quote-line ${variantClass}`}></div>
          <div className={`quote-author ${variantClass}`}>- {authorText} -</div>
        </div>
      </div> */}
    </>
  );
};

export default SliderOverlay;
